import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal, MaskedLines } from "@/components/Reveal";

const TEAM_IMG =
  "https://images.pexels.com/photos/8428082/pexels-photo-8428082.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Equipe() {
  const { t } = useTranslation("equipe");
  const facts = t("hero.facts", { returnObjects: true });
  const people = t("direction.people", { returnObjects: true });
  const members = t("committee.members", { returnObjects: true });
  const principles = t("principles.items", { returnObjects: true });

  return (
    <div data-testid="equipe-page" className="pt-32 lg:pt-40">
      <header className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <p className="overline-label mb-8 flex items-center gap-3">
          <span className="inline-block h-px w-10 bg-brass/60" />
          {t("hero.overline")}
        </p>
        <h1 className="max-w-3xl font-display text-4xl font-light leading-[1.08] tracking-tight text-chalk sm:text-5xl lg:text-6xl">
          <MaskedLines
            lines={[
              t("hero.titleA"),
              <span key="b" className="font-serif italic text-brass">{t("hero.titleB")}</span>,
            ]}
          />
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-mist/85">{t("hero.lead")}</p>

        <Reveal delay={0.2}>
          <div className="mt-10 grid items-center gap-8 lg:grid-cols-12">
            <div className="overflow-hidden border border-white/10 lg:col-span-5">
              <img
                src={TEAM_IMG}
                alt={t("hero.imgAlt")}
                className="h-64 w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-72"
              />
            </div>
            <ul className="flex flex-wrap gap-3 lg:col-span-7">
              {facts.map((f) => (
                <li
                  key={f}
                  className="border border-white/[0.1] px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-mist/75"
                >
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </header>

      <section className="mx-auto mt-24 max-w-7xl px-6 sm:px-8 lg:px-12 lg:mt-32">
        <Reveal>
          <p className="overline-label">{t("direction.overline")}</p>
        </Reveal>
        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          {people.map((d, i) => (
            <Reveal key={d.name} delay={i * 0.1}>
              <article
                data-testid={d.testid}
                className="h-full border border-white/[0.08] bg-surface/40 p-8 transition-colors duration-300 hover:border-brass/35 lg:p-12"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-brass/70">{d.role}</p>
                <h2 className="mt-4 font-display text-3xl text-chalk">{d.name}</h2>
                <p className="mt-6 text-sm leading-relaxed text-mist/85">{d.bio}</p>
                <p className="mt-4 border-l-2 border-brass/50 pl-5 font-serif text-base italic leading-relaxed text-slate2">
                  {d.vision}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal>
            <p className="overline-label">{t("committee.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal tracking-tight text-chalk sm:text-3xl">
              {t("committee.titleA")}
              <span className="font-serif italic text-brass">{t("committee.titleB")}</span>
            </h2>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate2">{t("committee.lead")}</p>
          </Reveal>
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {members.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.08}>
                <article
                  data-testid={`team-member-card-${i + 1}`}
                  className="flex h-full flex-col border border-white/[0.08] bg-ink p-8 transition-colors duration-300 hover:border-brass/35"
                >
                  <span className="flex h-12 w-12 items-center justify-center border border-brass/30 font-display text-base text-brass">
                    {m.initials}
                  </span>
                  <h3 className="mt-6 font-display text-xl text-chalk" data-testid={`team-member-name-${i + 1}`}>{m.name}</h3>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brass/80">{m.role}</p>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-slate2">{m.bio}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <Reveal className="max-w-3xl">
          <p className="overline-label">{t("approach.overline")}</p>
          <h2 className="mt-4 font-display text-2xl font-normal tracking-tight text-chalk sm:text-3xl">
            {t("approach.titleA")}
            <span className="font-serif italic text-brass">{t("approach.titleB")}</span>
          </h2>
          <p className="mt-6 text-sm leading-relaxed text-mist/85">{t("approach.lead")}</p>
        </Reveal>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal>
            <p className="overline-label">{t("principles.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal tracking-tight text-chalk sm:text-3xl">
              {t("principles.titleA")}
              <span className="font-serif italic text-brass">{t("principles.titleB")}</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06} className="bg-ink">
                <div data-testid={`leadership-principle-${i + 1}`} className="h-full p-8 transition-colors duration-300 hover:bg-elevated">
                  <p className="font-mono text-xs tracking-[0.24em] text-brass/70">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-5 font-display text-lg text-chalk">{p.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-slate2">{p.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/40">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-28">
          <Reveal className="max-w-3xl">
            <h2 className="font-display text-3xl font-light leading-[1.15] tracking-tight text-chalk sm:text-4xl">
              {t("final.titleA")}
              <span className="font-serif italic text-brass">{t("final.titleB")}</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-mist/85">{t("final.lead")}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="equipe-cta-appointment" className="btn-brass">
                {t("final.btnPrimary")} <ArrowUpRight size={15} />
              </Link>
              <Link to="/services" data-testid="equipe-cta-services" className="btn-ghost">
                {t("final.btnSecondary")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
